import { LinkNode, SerializedLinkNode } from '@lexical/link';
import { LexicalNode, NodeKey } from 'lexical';
import { defaultTranslations } from './LocalizableMixin.tsx';
import { Language } from 'services/api/api-client.types.ts';
import { $isProtectedParagraphNode } from 'pages/authorized/page-designer/lexical/nodes/Localizable/ProtectedParagraphNode.ts';
import { $isTabItemNode } from 'pages/authorized/page-designer/lexical/nodes/TabItemNode.ts';

export type SerializedLocalizationLinkNode = SerializedLinkNode & {
  translations: Record<string, string | null>;
  type: 'localization-link';
};

export class LocalizationLinkNode extends LinkNode {
  __translations: Record<string, string | null>;

  constructor(
    url?: string,
    key?: NodeKey,
    translations?: Record<string, string | null>,
  ) {
    super(url ?? '', undefined, key);
    this.__translations = {
      ...defaultTranslations,
      ...(translations || {}),
    };
  }

  setTranslations(language: Language, text: string) {
    const writable = this.getWritable();
    writable.__translations = {
      ...writable.__translations,
      [language]: text,
    };
    writable.markDirty();
  }

  getTranslation(language: Language) {
    return (
      this.__translations?.[language] ??
      this.__translations?.[language.toLowerCase()] ??
      null
    );
  }

  isInsideTab() {
    const paragraphNode = this.getParent();

    if ($isProtectedParagraphNode(paragraphNode)) {
      const tabItemNode = paragraphNode.getParent();

      if ($isTabItemNode(tabItemNode)) {
        return true;
      }
    }

    return false;
  }

  static getType(): string {
    return 'localization-link';
  }

  static clone(node: LocalizationLinkNode): LocalizationLinkNode {
    return new LocalizationLinkNode(node.__url, node.__key, {
      ...node.__translations,
    });
  }

  exportJSON(): SerializedLocalizationLinkNode {
    return {
      ...super.exportJSON(),
      type: 'localization-link',
      translations: this.__translations,
    };
  }

  static importJSON(
    serializedNode: SerializedLocalizationLinkNode,
  ): LocalizationLinkNode {
    const node = new LocalizationLinkNode(
      serializedNode.url ?? '',
      undefined,
      serializedNode.translations ?? undefined,
    );

    node.setFormat(serializedNode.format);
    node.setDirection(serializedNode.direction);
    node.setIndent(serializedNode.indent);
    node.setTarget((serializedNode as any).target ?? null);
    node.setRel((serializedNode as any).rel ?? null);
    node.setTitle((serializedNode as any).title ?? null);

    return node;
  }
}

export function $createLocalizationLinkNode(
  url?: string,
  translations?: Record<string, string | null>,
): LocalizationLinkNode {
  return new LocalizationLinkNode(url, undefined, translations);
}

export function $isLocalizationLinkNode(
  node: LexicalNode | null | undefined,
): node is LocalizationLinkNode {
  return node instanceof LocalizationLinkNode;
}
